<template>
    <v-container pa-0>
        <build-stepper-form ref="form-builder"
                            :stepper.sync="stepper"
                            :errors.sync="errors"
                            :steps="steps"
                            :completed.sync="completed"
                            :form.sync="form"
                            :rules="rules"/>

    </v-container>
</template>
<script>
    // components
    import BuildStepperForm from '../../components/Builders/BuildStepperForm';
    // utils
    import {
        api,
        parseErrors
    } from '../../utils/api';
    import {
        API_ENDPOINT_REGISTER_COMPANY_FINALIZE,
        API_ENDPOINT_REGISTER_COMPANY_PROCEED,
        API_ENDPOINT_REGISTER_COMPANY_START,
    } from '../../utils/constants';
    import {
        company_id,
        email,
        email_taxation_documentation,
        first_name,
        last_name,
        password,
        password_confirmation,
        terms_and_conditions,
        zip_code,
    } from '../../utils/formRules';
    import { setPageTitle } from '../../utils/helper';

    export default {
        name: 'register-company',
        components: {
            BuildStepperForm,
        },
        props: {
            token: {
                required: false,
                type: String,
            },
        },
        data() {
            return {
                stepper: 1,

                errors: {},

                completed: false,

                form: {
                    company_id: null,
                    zip_code: null,
                    first_name: null,
                    last_name: null,
                    email: null,
                    email_taxation_documentation: null,
                    password: null,
                    password_confirmation: null,
                },

                rules: {
                    company_id,
                    zip_code,
                    last_name,
                    first_name,
                    email,
                    password,
                    password_confirmation,
                    terms_and_conditions,
                },
            }
        },
        computed: {
            steps() {
                return [
                    // step 1
                    {
                        title: '$vuetify.register.company_step1.headline',
                        description: '$vuetify.register.company_step1.desc',
                        formItems: [
                            {
                                is: 'text',
                                name: 'company_id',
                            },
                            {
                                is: 'text',
                                name: 'zip_code',
                            },
                        ],
                        actions: [
                            {
                                is: 'button',
                                name: 'start_register',
                                color: 'primary',
                                value: '$vuetify.buttons.start_register',
                                clickMethod: this.submitStartRegister,
                            },
                        ],
                        completed: {
                            title: '$vuetify.register.company_step1_complete.headline',
                            description: '$vuetify.register.company_step1_complete.desc',
                        },
                    },

                    // step 2
                    {
                        title: '$vuetify.register.company_step2.headline',
                        description: '$vuetify.register.company_step2.desc',
                        formItems: [
                            {
                                is: 'text',
                                name: 'last_name',
                            },
                            {
                                is: 'text',
                                name: 'first_name',
                            },
                            {
                                is: 'text',
                                name: 'email',
                            },
                            {
                                is: 'subhead',
                                name: 'privateSection',
                                label: '$vuetify.register.company_step2.taxation_documentation_desc',
                                classes: 'mb-2',
                            },
                            {
                                is: 'text',
                                name: 'email_taxation_documentation',
                            },
                        ],
                        actions: [
                            {
                                is: 'next',
                            },
                        ],
                    },

                    // step 3
                    {
                        title: '$vuetify.register.company_step3.headline',
                        description: '$vuetify.register.company_step3.desc',
                        formItems: [
                            {
                                is: 'password',
                                name: 'password',
                            },
                            {
                                is: 'password',
                                name: 'password_confirmation',
                            },
                            {
                                is: 'checkbox',
                                label: '$vuetify.form_items.data_protection_and_conditions_of_participation',
                                linkName: '$vuetify.form_items.data_protection_link',
                                linkName2: '$vuetify.form_items.terms_link',
                                slug: 'data_protection',
                                slug2: 'terms',
                                tooltip: '$vuetify.tooltips.new_window',
                                name: 'terms_and_conditions',
                            },
                        ],
                        actions: [
                            {
                                is: 'previous',
                            },
                            {
                                is: 'button',
                                color: 'primary',
                                name: 'finalize',
                                clickMethod: this.submitFinalizeRegister,
                            },
                        ],
                        completed: {
                            title: '$vuetify.register.company_step3_complete.headline',
                            description: '$vuetify.register.company_step3_complete.desc',
                        },
                    },
                ];
            }
        },
        methods: {
            submitStartRegister() {
                this.errors = {};

                if (!this.$refs['form-builder'].validateCurrentFormBeforeSubmit()) return false;

                this.$wait.start('submit-register');

                // add current client locale to form
                this.form.locale = this.$i18n.locale;

                api.post(API_ENDPOINT_REGISTER_COMPANY_START, this.form)
                    .then(response => {
                        const { data } = response;
                        this.completed = true;
                        this.$wait.end('submit-register');
                    })
                    .catch(error => {
                        this.$wait.end('submit-register');
                        this.errors = parseErrors(error);
                    });
            },
            submitFinalizeRegister() {
                this.errors = {};

                if (!this.$refs['form-builder'].validateCurrentFormBeforeSubmit()) return false;

                this.$wait.start('submit-register');

                // add current client locale to form
                this.form.locale = this.$i18n.locale;

                api.post(API_ENDPOINT_REGISTER_COMPANY_FINALIZE, this.form)
                    .then(response => {
                        const { data } = response;
                        this.completed = true;
                        this.$wait.end('submit-register');
                    })
                    .catch(error => {
                        this.$wait.end('submit-register');
                        this.errors = parseErrors(error);
                    });
            },
        },
        mounted() {
            if (this.token) {
                api.get(`${API_ENDPOINT_REGISTER_COMPANY_PROCEED}${this.token}`)
                    .then(response => {
                        const { data } = response.data;
                        this.stepper = 2;
                        this.form.company_id = data.company_id;
                        this.form.zip_code = data.address_zip;
                        this.form.first_name = data.owner_firstname;
                        this.form.last_name = data.owner_lastname;
                        this.form.email = data.owner_email;
                    })
                    .catch(error => {
                        this.$router.push({ name: 'register-company' });
                    });
            }
        },
    }
</script>
